import { Monitor, Moon, Sun } from "@phosphor-icons/react"
import { createContext, memo, useCallback, useContext, useEffect, useMemo } from "react"
import { usePersistedState } from "./usePersistedState"

// Used for setting the theme-color meta tag so we don't have to figure out the
// computed color of the body
const themeColorLight = "hsl(0 0% 98%)"
const themeColorDark = "hsl(0 0% 2.5%)"

export const themeOptions = {
  light: {
    label: "Light",
    icon: Sun,
  },
  dark: {
    label: "Dark",
    icon: Moon,
  },
  system: {
    label: "System",
    icon: Monitor,
  },
} as const

export type ThemePreference = keyof typeof themeOptions

export type ThemeMode = "light" | "dark"

type ThemeContextType = {
  preferredTheme: ThemePreference | undefined
  theme: ThemeMode | undefined
  setTheme: (newTheme: ThemeMode) => void
  setPreferredTheme: (newPreferredTheme: ThemePreference) => void
}

declare global {
  interface Window {
    // Set by AvoidThemeFlickerScript before React renders
    __tt_theme?: ThemeMode
  }
}

const ThemeContext = createContext<ThemeContextType | null>(null)

export const ThemeProvider = ({
  children,
  forcedTheme,
}: {
  children: React.ReactNode
  // Only really used for the landing page, where we want to always use the dark theme
  forcedTheme?: ThemeMode
}) => {
  const [preferredTheme, _setPreferredTheme] = usePersistedState<ThemePreference | undefined>(
    "theme-preference",
    "dark",
    "localStorage",
  )
  const [_theme, _setTheme] = usePersistedState<ThemeMode | undefined>(
    "theme",
    // TODO: Consider using window.__tt_theme as default
    undefined,
    "localStorage",
  )

  const theme = forcedTheme ?? _theme

  // When storage is blocked the hook never learns the theme the inline script
  // applied, so take it from there instead of leaving consumers on undefined
  useEffect(() => {
    if (_theme === undefined && !forcedTheme) {
      _setTheme(window.__tt_theme ?? "dark")
    }
  }, [_theme, forcedTheme, _setTheme])

  // Updates the <meta name="theme-color" /> tag so browsers like Safari extend
  // the background color to the operating system UI above and below the page
  const updateMetaThemeColorTag = useCallback((newTheme: ThemeMode) => {
    const body = document.querySelector("body")
    if (body) {
      const bodyColor = newTheme === "dark" ? themeColorDark : themeColorLight
      const metaElement = document.querySelector("meta[name='theme-color']")
      if (metaElement) {
        metaElement.setAttribute("content", bodyColor)
      }
    }
  }, [])

  const updateBodyClass = useCallback(
    (newTheme: ThemeMode) => {
      const body = document.querySelector("body")
      if (!body) return

      body.classList.remove("theme-light", "theme-dark")
      body.classList.add(`theme-${newTheme}`)

      updateMetaThemeColorTag(newTheme)
    },
    [updateMetaThemeColorTag],
  )

  const setPreferredTheme = useCallback(
    (newPreferredTheme: ThemePreference) => {
      const body = document.querySelector("body")
      if (!body) return

      const prefersDarkMode = window.matchMedia("(prefers-color-scheme: dark)")

      body.classList.remove("theme-light", "theme-dark")
      let newTheme: ThemeMode
      if (newPreferredTheme && newPreferredTheme !== "system") {
        newTheme = newPreferredTheme
      } else if (newPreferredTheme && prefersDarkMode.matches) {
        newTheme = "dark"
      } else {
        newTheme = "light"
      }
      updateBodyClass(newTheme)

      _setTheme(newTheme)
      _setPreferredTheme(newPreferredTheme)
    },
    [_setPreferredTheme, _setTheme, updateBodyClass],
  )

  const setTheme = useCallback(
    (newTheme: ThemeMode) => {
      updateBodyClass(newTheme)
      _setTheme(newTheme)
    },
    [_setTheme, updateBodyClass],
  )

  useEffect(() => {
    // Only listen to color scheme changes if the user is using the system theme
    if (preferredTheme !== "system" || forcedTheme) {
      return
    }

    const handleMediaChange = (e: MediaQueryListEvent) => {
      setTheme(e.matches ? "dark" : "light")
    }
    const media = window.matchMedia("(prefers-color-scheme: dark)")
    media.addEventListener("change", handleMediaChange)
    return () => media.removeEventListener("change", handleMediaChange)
  }, [preferredTheme, setTheme, forcedTheme])

  const context: ThemeContextType = useMemo(
    () => ({ preferredTheme, theme, setTheme, setPreferredTheme }),
    [preferredTheme, theme, setTheme, setPreferredTheme],
  )

  return <ThemeContext.Provider value={context}>{children}</ThemeContext.Provider>
}

export const useThemeContext = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error("useThemeContext must be used within a ThemeProvider")
  }
  return context
}

// Script needs to be part of HTML to reduce screen flickering
export const AvoidThemeFlickerScript = memo(() => (
  <script
    dangerouslySetInnerHTML={{
      __html: `
      // AvoidThemeFlickerScript
      (function() {
        // Storage access throws when the browser blocks site data; fall back to dark
        let preference = null
        try {
          preference = JSON.parse(localStorage.getItem('tt.theme-preference'))
        } catch (e) {}
        let activeTheme

        if (preference === 'dark' || preference === 'light') {
          activeTheme = preference
        } else if (preference === 'system') {
          // Resolve system theme dynamically
          const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
          activeTheme = prefersDark ? 'dark' : 'light'
        } else {
          try { localStorage.setItem('tt.theme-preference', JSON.stringify("dark")) } catch (e) {}
          activeTheme = 'dark'
        }

        try { localStorage.setItem('tt.theme', JSON.stringify(activeTheme)) } catch (e) {}
        document.body.classList.add('theme-' + activeTheme)
        window.__tt_theme = activeTheme

        // Copy of updateMetaThemeColorTag but adds meta element directly
        let metaElement = document.querySelector("meta[name='theme-color']")
        if (!metaElement) {
          metaElement = document.createElement("meta")
          metaElement.name = "theme-color"
          document.head.appendChild(metaElement)
        }

        const body = document.querySelector("body")
        if (body) {
          const bodyColor = activeTheme === "dark" ? "${themeColorDark}" : "${themeColorLight}"
          const metaElement = document.querySelector("meta[name='theme-color']")
          if (metaElement) {
            metaElement.setAttribute("content", bodyColor)
          }
        }
      })()
    `,
    }}
  />
))

AvoidThemeFlickerScript.displayName = "AvoidThemeFlicker"
